import { NetworkType } from 'abis/set/sets'
import type Big from 'big.js'
import { ARTXAI, CAI, JMX } from 'products'
import type { ProductType } from 'sdk'
import type { CurrencyType } from 'utils'
import type { Address } from 'viem'
import { ePHTR } from './products'

/**
 * Given a resource directory, expect images to be found in the following format:
 * For cards: large_card/medium_card/small_card.png,
 * For index page: large_index_details/medium_index_details/small_index_details.png
 * For badges: chain_badge.png
 */

export type PermitTypes = 'eip2612' | 'dai' | undefined

export interface CurrencyMetadata {
  address: Address
  name: string
  symbol: string
  decimals: number
  icon: string
  defaultQuoteAmount: Big // TODO: check
  chainId: NetworkType
  type: CurrencyType
  coingeckoId: string | undefined
  permit: PermitTypes
}
export interface ProductMetadata {
  symbol: string
  longName: string
  tagLine: string
  resourceDirectory: string
  blockExplorer: string
  startDate: number
  methodologyLink: string
  icon: 'icon'
  description: string
  brandColour: string
  additionalBrandColour: string
  returnBarColour: string
  nativeTokenColour: string
  nativeTokenHoverColour: string
  network: NetworkType // index-details page needs to know what network each index belongs to
  productType: ProductType
  acceptedCurrencies: CurrencyMetadata[]
  productCurrency: CurrencyMetadata
  decimals: number
  productTheme: ProductTheme
  address: Address
  isCrossChain?: boolean
  defaultSlippagePercentage: number
  subgraph: string
}

interface IndexList {
  [key: string]: ProductMetadata
}

export type ProductTheme = 'LIGHT' | 'DARK'

export interface AssetMetaData {
  symbol: string
  longName: string
  tagLine: string
  description: string
  network: NetworkType
  productType: ProductType
  acceptedCurrencies: CurrencyMetadata[]
  productCurrency: CurrencyMetadata
  decimals: number
  productTheme: ProductTheme
  returnBarColour: string
  brandColour: string
  additionalBrandColour: string
  nativeTokenColour: string
  nativeTokenHoverColour: string
  blockExplorer: string
  address: Address
  methodologyLink: string
  isCrossChain?: boolean
  defaultSlippagePercentage: number
  type: CurrencyType
  startDate: number
  subgraph: string
}

/**
 * Factory for building the index assets list. providing the directory of the index assets.
 * @param name the name of the index
 * @param description a description of the index
 * @param network the network the index belongs to
 * @returns
 */
export const productAssetsFactory = (assetOptions: AssetMetaData): ProductMetadata => {
  const {
    address,
    symbol,
    longName,
    description,
    tagLine,
    network,
    productType,
    acceptedCurrencies,
    decimals,
    productTheme,
    brandColour,
    additionalBrandColour,
    returnBarColour,
    nativeTokenColour,
    nativeTokenHoverColour,
    blockExplorer,
    methodologyLink,
    isCrossChain,
    defaultSlippagePercentage,
    productCurrency,
    startDate,
    subgraph,
  } = assetOptions
  return {
    address,
    symbol,
    longName,
    tagLine,
    acceptedCurrencies,
    startDate,
    productCurrency: productCurrency,
    decimals,
    productTheme,
    brandColour,
    additionalBrandColour,
    returnBarColour,
    nativeTokenColour,
    nativeTokenHoverColour,
    blockExplorer,
    methodologyLink,
    isCrossChain,
    defaultSlippagePercentage,
    subgraph,
    resourceDirectory: `${symbol}`,
    icon: 'icon',
    description: description,
    network: network,
    productType: productType,
  }
}

export const helperContracts = {
  [NetworkType.Mainnet]: {
    USV: {
      priceViewerAddress: '0x313f7D2deF9e0b19Ec2fB3ae84c819dACd200Dca' as Address,
    },
  },
}

export const productList: IndexList = {
  '0x48f88a3fe843ccb0b5003e70b4192c1d7448bef0': productAssetsFactory(CAI),
  '0x3b9805E163b3750e7f13a26B06F030f2d3b799F5': productAssetsFactory(ePHTR),
  '0x8e991e9edc300a538ce72186f5bcbc94f4bb9c80': productAssetsFactory(ARTXAI),
  '0x83190636c344cf1220ffcbfc0c1198f7867b3667': productAssetsFactory(JMX),
}

// makes sure the address is lowercased
export const getProductByAddress = (address: string): ProductMetadata => {
  return productList[address.toLowerCase()]
}

// return all products as an array
export const getAllProducts = (): ProductMetadata[] => {
  return Object.values(productList)
}

export const getAllProductAddresses = (): Address[] => {
  return getAllProducts().map((el) => el.address)
}

// find a product by its short name
export const getProductBySymbol = (symbol: string): ProductMetadata | undefined => {
  return getAllProducts().find((product) => {
    return product.symbol.toLowerCase() === symbol.toLowerCase()
  })
}
